import React,
{
  useEffect,
  useState,
  useCallback
}
from "react";

import {
  Store,
  IdCard,
  User,
  MapPin,
  Phone,
  Calendar,
  ClipboardList,
  Pencil,
  QrCode,
  LocateFixed,
  UtensilsCrossed
} from "lucide-react";

import axios from "axios";

import "./OwnerDashboardHome.css";

const BASE_API =
  process.env.BASE_API_URL ||
  "https://scanmymenu-nm38.onrender.com/api";

export default function OwnerDashboardHome() {

  const restaurantId =
    localStorage.getItem(
      "restaurantId"
    );

  const [
    restaurant,
    setRestaurant
  ] = useState(null);

  const [
    itemCount,
    setItemCount
  ] = useState(0);

  const [
    loading,
    setLoading
  ] = useState(false);

  /*
  FETCH DASHBOARD
  */

  const fetchDashboard =
    useCallback(
      async () => {

        try {

          setLoading(true);

          const res =
            await axios.get(
              `${BASE_API}/restaurants/${restaurantId}`
            );

          setRestaurant(
            res.data
          );

          /*
          ITEMS COUNT
          */

          setItemCount(
            res.data.items
              ?.length || 0
          );

        }
        catch (error) {

          console.error(
            "Dashboard fetch error"
          );

        }
        finally {

          setLoading(false);

        }

      },
      [restaurantId]
    );

  useEffect(() => {

    fetchDashboard();

  }, [fetchDashboard]);

  /*
  PLAN STATUS
  */

  const isPlanActive =
    restaurant?.subscriptionStatus === "active";

  /*
  NAVIGATION
  */

  const goToMenu =
    () =>
      window.location.href =
        "/owner/menu";

  const goToEdit =
    () =>
      window.location.href =
        "/owner/edit";

  const goToQR =
    () =>
      window.location.href =
        "/owner/qr";

  if (loading) {

    return (

      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#f8f8f8",
          flexDirection: "column",
          gap: "16px",
        }}
      >

        <div
          style={{
            width: "55px",
            height: "55px",
            border: "5px solid #ffe0cc",
            borderTop: "5px solid #ff6b00",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />

        <p
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#444",
            letterSpacing: "0.5px",
          }}
        >
          Loading Dashboard...
        </p>

        <style>
          {`
            @keyframes spin {
              0% {
                transform: rotate(0deg);
              }
              100% {
                transform: rotate(360deg);
              }
            }
          `}
        </style>

      </div>

    );

  }

  return (

    <div className="home">

      {/* RESTAURANT CARD */}

      <div className="header-card">

        <img
          src={restaurant?.logoUrl}
          alt="Restaurant"
          className="header-image"
        />

        <div style={{ marginLeft: "10px" }}>

          <h3 style={{ margin: 0, marginTop: 10 }}>
            {restaurant?.restaurantName}
          </h3>

          <p
            style={{
              fontSize: "14px",
              opacity: 0.8,
              alignItems: "center",
              display: "flex",
              gap: 4,
              marginBottom: 0
            }}
          >

            <LocateFixed size={16} />

            {restaurant?.restaurantAddress}

          </p>

          <p
            style={{
              fontSize: "12px",
              fontWeight: "600",
              padding: "6px 12px",
              borderRadius: "20px",
              display: "inline-block",
              backgroundColor:
                restaurant?.subscriptionStatus === "active"
                  ? "#e8f9ee"
                  : "#ffeaea",
              color:
                restaurant?.subscriptionStatus === "active"
                  ? "#16a34a"
                  : "#dc2626",
              border:
                restaurant?.subscriptionStatus === "active"
                  ? "1px solid #bbf7d0"
                  : "1px solid #fecaca",
            }}
          >
            {restaurant?.subscriptionStatus === "active"
              ? "● Active"
              : "● Inactive"}
          </p>

        </div>

      </div>

      <div className="content">

        <div className="stat-card">

          <div className="icon-box">
            <UtensilsCrossed size={22} />
          </div>

          <div>
            <p className="label">
              Total Items
            </p>

            <h2>
              {itemCount}
            </h2>
          </div>

        </div>

        <div className="stat-card">

          <div className="icon-box scans">
            <QrCode size={22} />
          </div>

          <div>
            <p className="label">
              Total Scans
            </p>

            <h2>
              {restaurant?.totalScans || 0}
            </h2>
          </div>

        </div>

      </div>

      {/* QUICK ACCESS */}

      <div className="quick-card">

        <div className="quick-title">
          Quick Access
        </div>

        <div className="quick-actions">

          {/* MENU */}

          <div
            className={`quick-item ${
              !isPlanActive
                ? "disabled-card"
                : ""
            }`}
            onClick={() =>
              isPlanActive &&
              goToMenu()
            }
          >

            <ClipboardList />

            <p style={{ fontSize: "14px" }}>
              Manage Menu
            </p>

          </div>

          {/* EDIT */}

          <div
            className={`quick-item ${
              !isPlanActive
                ? "disabled-card"
                : ""
            }`}
            onClick={() =>
              isPlanActive &&
              goToEdit()
            }
          >

            <Pencil />

            <p style={{ fontSize: "14px" }}>
              Edit Restaurant
            </p>

          </div>

          {/* QR */}

          <div
            className={`quick-item ${
              !isPlanActive
                ? "disabled-card"
                : ""
            }`}
            onClick={() =>
              isPlanActive &&
              goToQR()
            }
          >

            <QrCode />

            <p style={{ fontSize: "14px" }}>
              QR Code
            </p>

          </div>

        </div>

        {/* INACTIVE MESSAGE */}

        {!isPlanActive && (

          <p
            style={{
              color: "#dc2626",
              fontSize: "13px",
              marginTop: "14px",
              fontWeight: "600",
              textAlign: "center"
            }}
          >
            Your subscription is inactive.
            Please upgrade your plan.
          </p>

        )}

      </div>

      <div className="restaurant-card">

        {/* HEADER */}

        <div className="card-header">

          <div className="title">

            <Store size={18} />

            Restaurant Information

          </div>

          <button
            style={{
              background: "transparent",
              border: "none",
              cursor:
                !isPlanActive
                  ? "not-allowed"
                  : "pointer",
              opacity:
                !isPlanActive
                  ? 0.5
                  : 1
            }}
            onClick={() =>
              isPlanActive &&
              goToEdit()
            }
            disabled={!isPlanActive}
          >

            <Pencil size={16} />

          </button>

        </div>

        <hr />

        <div className="card-body">

          {/* LEFT DETAILS */}

          <div className="details">

            <div className="row">

              <User className="icon" />

              <div>

                <span>
                  Name
                </span>

                <p style={{ fontSize: "24px" }}>
                  {restaurant?.restaurantName}
                </p>

              </div>

            </div>

            <div className="row">

              <IdCard className="icon" />

              <div>

                <span>
                  Restaurant ID
                </span>

                <p>
                  {restaurant?.id}
                </p>

              </div>

            </div>

            <div className="row">

              <MapPin className="icon" />

              <div>

                <span>
                  Address
                </span>

                <p>
                  {restaurant?.restaurantAddress}
                </p>

              </div>

            </div>

            <div className="row">

              <Phone className="icon" />

              <div>

                <span>
                  Phone
                </span>

                <p>
                  {restaurant?.phone}
                </p>

              </div>

            </div>

            <div className="row">

              <Calendar className="icon" />

              <div>

                <span>
                  Joined On
                </span>

                <p>
                  {restaurant?.createdAt
                    ? new Date(
                        restaurant.createdAt
                      ).toLocaleDateString()
                    : "N/A"}
                </p>

              </div>

            </div>

          </div>

        </div>

      </div>

    </div>

  );

}